const amount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isAmount = (value) => Number.isFinite(Number(value));
const reconciles = (left, right) => Math.abs(left - right) <= 0.011;
const roundMoney = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Collapsed "Taxes & fees" amount for confirmation / email summaries.
 * Includes tax + platform fees + one-way + after-hours so base + this line = total
 * (before discounts). Checkout still itemizes these separately for clarity.
 */
export function inclusiveTaxesAndFees({
  taxesAndFees = 0,
  oneWayFee = 0,
  afterHoursPickupFee = 0,
  afterHoursReturnFee = 0,
  afterHoursFee = null,
} = {}) {
  const afterHours = afterHoursFee != null
    ? amount(afterHoursFee)
    : amount(afterHoursPickupFee) + amount(afterHoursReturnFee);
  return roundMoney(amount(taxesAndFees) + amount(oneWayFee) + afterHours);
}

/** Renter-facing amounts from the authoritative booking quote. */
export function bookingQuoteDisplay(quote) {
  if (!quote) return null;
  const breakdown = quote.price_breakdown || {};
  const afterHoursPickupFee = amount(breakdown.after_hours_pickup_fee);
  const afterHoursReturnFee = amount(breakdown.after_hours_return_fee);
  const afterHoursFee = afterHoursPickupFee + afterHoursReturnFee;
  const taxesAndFees = amount(breakdown.taxes_and_fees);
  const oneWayFee = amount(breakdown.one_way_fee);
  return {
    subtotal: amount(quote.subtotal),
    oneWayFee,
    afterHoursPickupFee,
    afterHoursReturnFee,
    afterHoursFee,
    taxesAndFees,
    /** tax + service + one-way + after-hours (for collapsed summaries) */
    taxesAndFeesInclusive: inclusiveTaxesAndFees({
      taxesAndFees,
      oneWayFee,
      afterHoursPickupFee,
      afterHoursReturnFee,
    }),
    taxAmount: amount(breakdown.tax_amount),
    otherFees: amount(breakdown.other_fees ?? breakdown.platform_fee),
    preDiscountTotal: amount(quote.pre_discount_total),
    zctDiscount: amount(quote.discount),
    redeemedZct: amount(quote.redeemed_zct),
    couponDiscount: amount(quote.coupon_discount),
    total: amount(quote.total ?? quote.estimated_total),
  };
}

/** Reject incomplete or internally inconsistent responses before enabling payment. */
export function isAuthoritativeTripQuote(quote) {
  if (!quote || !quote.price_breakdown) return false;
  const breakdown = quote.price_breakdown;
  const required = [
    quote.subtotal,
    quote.pre_discount_total,
    quote.discount,
    quote.redeemed_zct,
    quote.coupon_discount,
    quote.total ?? quote.estimated_total,
    breakdown.one_way_fee,
    breakdown.after_hours_return_fee,
    breakdown.taxes_and_fees,
    breakdown.tax_amount,
    breakdown.other_fees ?? breakdown.platform_fee,
  ];
  // Pickup fee is required on new quotes; legacy quotes without the field still validate.
  if ("after_hours_pickup_fee" in breakdown && !isAmount(breakdown.after_hours_pickup_fee)) {
    return false;
  }
  if (!required.every(isAmount)) return false;

  const displayed = bookingQuoteDisplay(quote);
  const feesReconcile = reconciles(
    displayed.taxesAndFees,
    displayed.taxAmount + displayed.otherFees,
  );
  const preDiscountReconciles = reconciles(
    displayed.preDiscountTotal,
    displayed.subtotal
      + displayed.oneWayFee
      + displayed.afterHoursFee
      + displayed.taxesAndFees,
  );
  const totalReconciles = reconciles(
    displayed.total,
    displayed.preDiscountTotal - displayed.zctDiscount - displayed.couponDiscount,
  );
  return feesReconcile && preDiscountReconciles && totalReconciles;
}

/** Payment is allowed only for a successful quote for the current inputs. */
export function canPayWithTripQuote({ quoteEnabled, quoteValid, loading, quoteError }) {
  return Boolean(quoteEnabled && quoteValid && !loading && !quoteError);
}
